/**
 * Prompts & Workflows Data
 *
 * All AI workflow definitions and prompt content used throughout the system.
 * Prompts are copied verbatim from backend source files.
 */

// ─── Types ───────────────────────────────────────────────────────────

export type WorkflowId = 'web_crawler' | 'report_generation' | 'evaluation' | 'conversation';

export interface WorkflowStep {
  id: string;
  label: string;
  description: string;
  service: string;
  icon: 'globe' | 'search' | 'scrape' | 'upload' | 'database' | 'brain' | 'file' | 'chat' | 'sparkles' | 'list' | 'merge';
  color: string;
  promptIds: string[];
}

export interface WorkflowDefinition {
  id: WorkflowId;
  name: string;
  description: string;
  icon: 'globe' | 'file' | 'brain' | 'chat';
  steps: WorkflowStep[];
}

export type PromptCategory =
  | 'evaluation_system'
  | 'evaluation_criteria'
  | 'evaluation_output'
  | 'report_themes'
  | 'report_synthesis'
  | 'conversation'
  | 'custom_formats'
  | 'search_config';

export interface PromptDefinition {
  id: string;
  name: string;
  category: PromptCategory;
  description: string;
  sourceFile: string;
  usedIn: WorkflowId[];
  promptText: string;
}

// ─── Prompt Category Labels ──────────────────────────────────────────

export const PROMPT_CATEGORY_LABELS: Record<PromptCategory, string> = {
  evaluation_system: 'Evaluation System',
  evaluation_criteria: 'Evaluation Criteria',
  evaluation_output: 'Evaluation Output',
  report_themes: 'Report Themes',
  report_synthesis: 'Report Synthesis',
  conversation: 'Conversation',
  custom_formats: 'Custom Formats',
  search_config: 'Search Config',
};

// ─── Prompts ─────────────────────────────────────────────────────────

export const PROMPTS: Record<string, PromptDefinition> = {
  // --- Evaluation System ---
  evaluation_system_prompt: {
    id: 'evaluation_system_prompt',
    name: 'Evaluation System Prompt',
    category: 'evaluation_system',
    description: 'Core system prompt that frames the evaluator as an expert analyst helping Fortune 100 executives evaluate AI-powered SaaS vendors.',
    sourceFile: 'packages/shared/src/constants/evaluation-criteria.ts',
    usedIn: ['evaluation'],
    promptText: `You are an expert analyst helping Fortune 100 executives evaluate B2B AI-powered SaaS software vendors. You generate evaluation reports grounded in a distinctive framework that assesses vendors not only on immediate utility, but on whether they accelerate or impede the organization's long-term capacity to work effectively with AI.

You are an analyst, not a decision-maker. Your role is to illuminate trade-offs and provide information that supports informed decisions—not to make buy/don't buy recommendations.

## Foundational Philosophy

AI-powered SaaS is fundamentally different from traditional SaaS:

1. **The Economics Have Changed**: AI SaaS introduces meaningful cost of goods sold (compute, inference, API calls). This creates misaligned incentives—vendors may quietly optimize for reduced usage or cheaper inference paths.

2. **The Moat Has Eroded**: Core AI functionality now rests on commodity foundation models. This creates strong vendor incentives to manufacture artificial lock-in through proprietary data formats and tool-specific workflows.

3. **The Stakes Are Organizational**: The companies that thrive will develop deep organizational fluency in AI. A vendor solution that delivers a quick win may simultaneously delay the organizational learning that will matter far more in the long run.

## What Excellent Vendors Look Like

The best AI-powered tools:
- Identify a specific, worthwhile problem and solve it in a distinctive way
- Locate their value honestly—in workflow design, domain expertise, integration quality, not in the AI itself
- Are transparent about commodity components because they're confident in their genuine value-add
- Don't hide AI mechanics—obscuring prompts, models, and architecture often signals thin differentiation

## Scoring Methodology

For each criterion, assign one of these scores:
- **Yes**: Clear, verifiable evidence that this criterion is met
- **Partially**: Some evidence exists, but with meaningful gaps or caveats
- **No**: Clear evidence that this criterion is not met
- **Insufficient Information**: Not enough information to assess—specify what would be needed

## Critical Rules

1. Default to "Insufficient Information" when uncertain. Do not infer or speculate.
2. Frame missing information as questions for the vendor.
3. Never fabricate information.
4. Distinguish claims from evidence—marketing claims are not evidence.
5. Present trade-offs, not verdicts. Different organizations will reasonably make different choices.

## Tone

Be direct and declarative. State conclusions first, then support them. No corporate hedging language. Not hostile, but not polite at the expense of clarity. If something is a problem, call it a problem.`,
  },

  // --- Evaluation Criteria (6 prompts) ---
  criterion_see: {
    id: 'criterion_see',
    name: 'SEE - Transparency & Visibility',
    category: 'evaluation_criteria',
    description: 'Evaluates whether the vendor exposes how their AI operates: prompts, models, context, and configuration.',
    sourceFile: 'packages/shared/src/constants/evaluation-criteria.ts',
    usedIn: ['evaluation'],
    promptText: `Evaluate the SEE (Transparency) criterion for this vendor.

Assess whether the vendor exposes the fundamental mechanics of how their AI operates:
- Can you see the prompts being used, including system prompts?
- Can you see which models are being selected and the logic behind model selection?
- Can you see how context is provided to the model—RAG systems, chunking strategies, retrieval approaches?
- Can you see inference parameters and other configuration choices?

Score as:
- "yes": Clear, verifiable evidence that transparency is provided
- "partially": Some visibility exists but with meaningful gaps
- "no": No visibility into AI operations, operates as a black box
- "insufficient": Cannot assess due to lack of information

Provide:
1. A 2-4 sentence assessment with specific evidence
2. Trade-off statement (what org gains vs gives up)
3. 2-3 specific questions to ask the vendor if gaps exist`,
  },

  criterion_change: {
    id: 'criterion_change',
    name: 'CHANGE - Customizability & Control',
    category: 'evaluation_criteria',
    description: 'Assesses the degree to which you can modify the tool\'s AI operations: prompts, models, context, and parameters.',
    sourceFile: 'packages/shared/src/constants/evaluation-criteria.ts',
    usedIn: ['evaluation'],
    promptText: `Evaluate the CHANGE (Customizability) criterion for this vendor.

Assess the degree to which you can modify the tool's AI operations:
- Can you adjust or replace system prompts?
- Can you select different models or model versions?
- Can you modify how context is retrieved, chunked, or prioritized?
- Can you adjust inference parameters?

Score as:
- "yes": Full customization of AI layer available
- "partially": Some customization exists but core AI layer cannot be modified
- "no": No ability to customize AI behavior
- "insufficient": Cannot assess due to lack of information

Provide:
1. A 2-4 sentence assessment with specific evidence
2. Trade-off statement (what org gains vs gives up)
3. 2-3 specific questions to ask the vendor if gaps exist`,
  },

  criterion_adapt: {
    id: 'criterion_adapt',
    name: 'ADAPT - Vendor Agility & Innovation',
    category: 'evaluation_criteria',
    description: 'Measures how quickly the vendor incorporates advances in the AI landscape.',
    sourceFile: 'packages/shared/src/constants/evaluation-criteria.ts',
    usedIn: ['evaluation'],
    promptText: `Evaluate the ADAPT (Vendor Agility) criterion for this vendor.

Assess how quickly the vendor incorporates advances in the AI landscape:
- How rapidly do they adopt new foundation models when superior options emerge?
- Do they update their approaches as new techniques and paradigms develop?
- Is there evidence of continuous improvement aligned with the pace of AI advancement?
- What is their typical update cycle compared to frontier model releases?

Score as:
- "yes": Rapid adoption of new capabilities, keeps pace with AI advancement
- "partially": Updates occur but lag behind frontier capabilities
- "no": Slow to adopt new models and techniques
- "insufficient": Cannot assess due to lack of information

Provide:
1. A 2-4 sentence assessment with specific evidence
2. Trade-off statement (what org gains vs gives up)
3. 2-3 specific questions to ask the vendor if gaps exist`,
  },

  criterion_use: {
    id: 'criterion_use',
    name: 'USE - Genuine Utility & Value',
    category: 'evaluation_criteria',
    description: 'Determines whether the tool delivers genuine value versus the appearance of value.',
    sourceFile: 'packages/shared/src/constants/evaluation-criteria.ts',
    usedIn: ['evaluation'],
    promptText: `Evaluate the USE (Genuine Utility) criterion for this vendor.

Assess whether the tool delivers genuine value versus the appearance of value:
- Does it produce measurably better outcomes than alternatives (including simpler approaches)?
- Is there evidence of real productivity gains, or does it merely feel productive?
- Does it solve problems that actually matter to the business?
- Are there customer case studies or testimonials demonstrating concrete value?

Score as:
- "yes": Clear evidence of genuine utility and measurable value
- "partially": Some utility demonstrated but with caveats or limitations
- "no": Appearance of value without substance
- "insufficient": Cannot assess due to lack of information

Provide:
1. A 2-4 sentence assessment with specific evidence
2. Trade-off statement (what org gains vs gives up)
3. 2-3 specific questions to ask the vendor if gaps exist`,
  },

  criterion_learn: {
    id: 'criterion_learn',
    name: 'LEARN - Transferable Knowledge',
    category: 'evaluation_criteria',
    description: 'Evaluates whether using the tool builds generalizable AI capabilities that transfer beyond the vendor.',
    sourceFile: 'packages/shared/src/constants/evaluation-criteria.ts',
    usedIn: ['evaluation'],
    promptText: `Evaluate the LEARN (Transferable Knowledge) criterion for this vendor.

Assess whether using this tool builds generalizable AI capabilities:
- Does working with this tool help users understand broad AI principles—prompting, context management, model selection, evaluation?
- Or does it only teach users how to operate this specific tool's interface and workflow?
- Do users come away with skills portable to other AI tools and internal development?

Score as:
- "yes": Tool builds transferable AI skills and understanding
- "partially": Some learning value but limited to specific features
- "no": Only teaches how to use this specific tool
- "insufficient": Cannot assess due to lack of information

Provide:
1. A 2-4 sentence assessment with specific evidence
2. Trade-off statement (what org gains vs gives up)
3. 2-3 specific questions to ask the vendor if gaps exist`,
  },

  criterion_exit: {
    id: 'criterion_exit',
    name: 'EXIT - Portability & Migration',
    category: 'evaluation_criteria',
    description: 'Assesses the difficulty of transitioning away from the vendor: data formats, exports, and lock-in.',
    sourceFile: 'packages/shared/src/constants/evaluation-criteria.ts',
    usedIn: ['evaluation'],
    promptText: `Evaluate the EXIT (Portability) criterion for this vendor.

Assess the difficulty of transitioning away from this vendor:
- Does the tool use proprietary data formats that would require conversion or be lost?
- Does it create artifacts in proprietary formats or languages?
- Can data and workflows be exported in standard, usable formats?
- How much institutional knowledge would be trapped if you left?

Score as:
- "yes": Easy migration with standard export formats
- "partially": Some data portable but with significant limitations
- "no": Data and configurations locked in proprietary formats
- "insufficient": Cannot assess due to lack of information

Provide:
1. A 2-4 sentence assessment with specific evidence
2. Trade-off statement (what org gains vs gives up)
3. 2-3 specific questions to ask the vendor if gaps exist`,
  },

  // --- Evaluation Output ---
  executive_summary_prompt: {
    id: 'executive_summary_prompt',
    name: 'Executive Summary',
    category: 'evaluation_output',
    description: 'Generates the executive summary section of an evaluation, synthesizing all criterion scores into a cohesive narrative.',
    sourceFile: 'packages/shared/src/constants/evaluation-criteria.ts',
    usedIn: ['evaluation'],
    promptText: `Based on the criterion evaluations provided, generate an executive summary for this vendor evaluation.

The summary should:
1. Open with a single, direct statement that answers: "What should we know about this tool, and what trade-offs does it present?"
2. Follow with 2-3 short paragraphs expanding on key strengths and trade-offs
3. Connect observations to the capacity-building framework
4. End with a clear statement of what type of organization this tool would or would not suit

Example opening lines:
- "Vendor X provides strong access to company information, but the opacity of its prompts and model choices raises concerns about long-term capacity building."
- "This tool solves a real problem effectively, but creates significant lock-in through proprietary data formats."
- "A strong choice for organizations prioritizing immediate productivity, with trade-offs for those prioritizing AI capability development."

Determine the overall sentiment:
- "positive": Tool is well-suited with minimal concerns
- "neutral": Meaningful trade-offs that depend on organizational priorities
- "negative": Significant concerns that warrant careful consideration`,
  },

  key_questions_prompt: {
    id: 'key_questions_prompt',
    name: 'Key Questions for Decision Makers',
    category: 'evaluation_output',
    description: 'Generates strategic questions that help decision-makers determine if a tool is right for their specific organization.',
    sourceFile: 'packages/shared/src/constants/evaluation-criteria.ts',
    usedIn: ['evaluation'],
    promptText: `Based on the evaluation, generate 5-6 strategic questions that decision-makers should consider based on their organizational context and priorities.

These questions should help the reader determine if this tool is right for THEIR organization specifically.

Example questions:
- How critical is [pain point] as an immediate priority versus a problem you could solve incrementally?
- Is your organization actively building AI capacity now, or is that a future priority?
- What is your tolerance for vendor lock-in given the rapid pace of AI advancement?
- Would this tool be a primary AI touchpoint for employees, or part of a portfolio approach?
- How do you evaluate the trade-off between immediate productivity gains and long-term capability development?`,
  },

  data_gathering_template: {
    id: 'data_gathering_template',
    name: 'Criterion Data Gathering',
    category: 'evaluation_output',
    description: 'Template used to query the vendor knowledge base for information relevant to each evaluation criterion. Uses {criterionName}, {criterionSubtitle}, and {evaluationPrompt} placeholders.',
    sourceFile: 'apps/evaluator/src/evaluator.agent.ts',
    usedIn: ['evaluation'],
    promptText: `For evaluating {criterionName} ({criterionSubtitle}):
{evaluationPrompt}

Please provide all relevant information from this vendor's website that would help assess this criterion.

Note: {criterionName}, {criterionSubtitle}, and {evaluationPrompt} are replaced at runtime with the specific criterion being evaluated.`,
  },

  structured_eval_template: {
    id: 'structured_eval_template',
    name: 'Structured Evaluation Template',
    category: 'evaluation_output',
    description: 'Template that combines the system prompt, criterion details, and vendor data to produce a structured evaluation with scores. Uses runtime variables for vendor-specific context.',
    sourceFile: 'apps/evaluator/src/evaluator.agent.ts',
    usedIn: ['evaluation'],
    promptText: `{systemPrompt}

You are evaluating {vendorName} for the {criterionName} criterion.

## Criterion: {criterionName} - {criterionSubtitle}
{criterionDescription}

## Evaluation Framework
{evaluationPrompt}

## Vendor Data Collected
{vendorData}

Based on this information, provide your evaluation. Be direct and specific. If information is missing, default to "insufficient" and specify what questions to ask the vendor.

Note: Placeholders like {vendorName} are replaced at runtime. The output is structured JSON with fields: score, assessment, tradeoff_gain, tradeoff_give_up, and questions.`,
  },

  // --- Custom Formats ---
  infographic_format: {
    id: 'infographic_format',
    name: 'Infographic Generation',
    category: 'custom_formats',
    description: 'Prompt for generating a professional infographic summarizing a vendor evaluation.',
    sourceFile: 'packages/shared/src/constants/evaluation-criteria.ts',
    usedIn: ['evaluation'],
    promptText: `Create a professional infographic summarizing this vendor evaluation.

The infographic should include:
1. Vendor name and category prominently displayed
2. Overall sentiment indicator (positive/neutral/negative)
3. Six evaluation criteria with visual score indicators:
   - SEE (Transparency)
   - CHANGE (Customizability)
   - ADAPT (Vendor Agility)
   - USE (Genuine Utility)
   - LEARN (Transferable Knowledge)
   - EXIT (Portability)
4. Key takeaway highlighted
5. Top 3 strengths and top 3 concerns
6. Visual trade-off summary

Style: Clean, modern, professional business infographic. Use a color scheme with:
- Green for positive scores
- Yellow/amber for partial scores
- Red for negative scores
- Professional navy/gray for structure

The design should be suitable for executive presentations.`,
  },

  // --- Report Themes (16 prompts) ---
  theme_company_overview: {
    id: 'theme_company_overview',
    name: 'Company Overview',
    category: 'report_themes',
    description: 'General information about the company, history, and mission.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'Provide a comprehensive overview of this company including their mission, history, size, and market positioning.',
  },

  theme_products_services: {
    id: 'theme_products_services',
    name: 'Products & Services',
    category: 'report_themes',
    description: 'Detailed breakdown of offerings.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'List and describe all products and services offered by this company, including key features and use cases.',
  },

  theme_pricing_model: {
    id: 'theme_pricing_model',
    name: 'Pricing Model',
    category: 'report_themes',
    description: 'Pricing structure, tiers, and licensing.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'Describe the pricing model, tiers, and any available pricing information. Include licensing terms if available.',
  },

  theme_target_market: {
    id: 'theme_target_market',
    name: 'Target Market',
    category: 'report_themes',
    description: 'Ideal customers and market segments.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'Identify the target market, ideal customer profiles, and market segments this company serves.',
  },

  theme_competitive_advantages: {
    id: 'theme_competitive_advantages',
    name: 'Competitive Advantages',
    category: 'report_themes',
    description: 'Unique selling points and differentiators.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'What are the key competitive advantages and unique selling points that differentiate this company from competitors?',
  },

  theme_technology_stack: {
    id: 'theme_technology_stack',
    name: 'Technology Stack',
    category: 'report_themes',
    description: 'Technical architecture and infrastructure.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'Describe the technology stack, infrastructure, and technical architecture used by this company.',
  },

  theme_integration_capabilities: {
    id: 'theme_integration_capabilities',
    name: 'Integration Capabilities',
    category: 'report_themes',
    description: 'APIs, connectors, and third-party integrations.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'What integration capabilities are available? List APIs, connectors, and supported third-party integrations.',
  },

  theme_security_compliance: {
    id: 'theme_security_compliance',
    name: 'Security & Compliance',
    category: 'report_themes',
    description: 'Security certifications and compliance standards.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'What security measures, certifications, and compliance standards does this company adhere to?',
  },

  theme_customer_testimonials: {
    id: 'theme_customer_testimonials',
    name: 'Customer Testimonials',
    category: 'report_themes',
    description: 'Case studies and customer success stories.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'Provide any customer testimonials, case studies, or success stories mentioned on the website.',
  },

  theme_leadership_team: {
    id: 'theme_leadership_team',
    name: 'Leadership Team',
    category: 'report_themes',
    description: 'Executive team and key personnel.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'Who are the key members of the leadership team? Include their roles and backgrounds if available.',
  },

  theme_funding_financials: {
    id: 'theme_funding_financials',
    name: 'Funding & Financials',
    category: 'report_themes',
    description: 'Investment history and financial information.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'What is known about the company funding history, investors, and financial status?',
  },

  theme_partnerships: {
    id: 'theme_partnerships',
    name: 'Partnerships',
    category: 'report_themes',
    description: 'Strategic partnerships and ecosystem.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'List any strategic partnerships, alliances, or ecosystem relationships.',
  },

  theme_geographic_presence: {
    id: 'theme_geographic_presence',
    name: 'Geographic Presence',
    category: 'report_themes',
    description: 'Office locations and market coverage.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'Where does this company operate? Include office locations and geographic market coverage.',
  },

  theme_support_sla: {
    id: 'theme_support_sla',
    name: 'Support & SLA',
    category: 'report_themes',
    description: 'Customer support and service level agreements.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'Describe the customer support offerings, SLA terms, and support channels available.',
  },

  theme_roadmap_vision: {
    id: 'theme_roadmap_vision',
    name: 'Roadmap & Vision',
    category: 'report_themes',
    description: 'Future plans and product roadmap.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'What is known about the company future plans, product roadmap, and strategic vision?',
  },

  theme_risk_factors: {
    id: 'theme_risk_factors',
    name: 'Risk Factors',
    category: 'report_themes',
    description: 'Potential risks and concerns.',
    sourceFile: 'packages/shared/src/constants/themes.ts',
    usedIn: ['report_generation'],
    promptText: 'Identify any potential risks, concerns, or red flags based on the available information.',
  },

  // --- Report Synthesis ---
  report_theme_synthesis: {
    id: 'report_theme_synthesis',
    name: 'Theme Synthesis',
    category: 'report_synthesis',
    description: 'Synthesizes vendor responses for a specific theme into a cohesive summary. Uses {themeName} and vendor response data at runtime.',
    sourceFile: 'apps/api/src/reports/reports.service.ts',
    usedIn: ['report_generation'],
    promptText: `Synthesize the following vendor responses about "{themeName}" into a cohesive summary.
Highlight key insights, common themes, and notable differences between vendors.
Be concise but comprehensive.

### {vendorName1}
{vendorResponse1}

### {vendorName2}
{vendorResponse2}

Provide a synthesized summary:

Note: {themeName}, {vendorName}, and {vendorResponse} are replaced at runtime with actual vendor data.`,
  },

  report_executive_summary: {
    id: 'report_executive_summary',
    name: 'Report Executive Summary',
    category: 'report_synthesis',
    description: 'Generates an executive summary from all report section summaries.',
    sourceFile: 'apps/api/src/reports/reports.service.ts',
    usedIn: ['report_generation'],
    promptText: `Based on the following section summaries, write a brief executive summary (2-3 paragraphs) that captures the key insights:

- {themeName1}: {sectionSummary1}...
- {themeName2}: {sectionSummary2}...

Executive Summary:

Note: Section summaries are injected at runtime from the completed theme analyses.`,
  },

  // --- Conversation ---
  conversation_synthesis: {
    id: 'conversation_synthesis',
    name: 'Conversation Synthesis',
    category: 'conversation',
    description: 'Synthesizes vendor information to answer user questions in chat. Combines responses from multiple vendors into a cohesive answer.',
    sourceFile: 'apps/api/src/conversations/conversations.service.ts',
    usedIn: ['conversation'],
    promptText: `Based on the following vendor information, provide a comprehensive answer to the user's question.
Be helpful, accurate, and cite specific vendors when appropriate.

USER QUESTION: {userQuery}

VENDOR INFORMATION:
### {vendorName1}
{vendorExcerpt1}

### {vendorName2}
{vendorExcerpt2}

RESPONSE:

Note: {userQuery}, {vendorName}, and {vendorExcerpt} are replaced at runtime with the actual user question and vendor data.`,
  },

  // --- Search Config ---
  tavily_search_config: {
    id: 'tavily_search_config',
    name: 'Tavily Search Strategy',
    category: 'search_config',
    description: 'Two-phase search strategy used by the Collector to discover vendor web pages, prioritizing documentation and technical content.',
    sourceFile: 'apps/collector/src/services/tavily.service.ts',
    usedIn: ['web_crawler'],
    promptText: `TAVILY SEARCH QUERIES
====================

1. Documentation Search Query:
   "site:{domain} (documentation OR "API reference" OR "user guide" OR "getting started" OR docs)"

   This query specifically targets documentation pages, which typically contain
   the most valuable technical information about a vendor's product.
   Allocates 1/3 of max results to documentation search.

2. General Search Query:
   Direct URL search with configurable depth (basic or advanced)

   This finds the main pages, blog posts, and other content on the vendor's site.

Both searches run in parallel for performance.

URL PRIORITIZATION PATTERNS
===========================

The Collector boosts relevance scores (+0.2, capped at 1.0) for URLs matching these patterns:
- /docs/ or /documentation/
- /api-reference/ or /api/
- /help/ or /guide/
- /manual/ or /reference/
- /tutorial/ or /getting-started/
- /quickstart/
- Subdomains: docs.*, help.*, developer.*

Documentation results are placed first, then general results are deduplicated and appended.
Final results are sorted by relevance score.

FIRECRAWL CONFIGURATION
=======================

Each URL is scraped with these settings:
- Format: markdown (clean text extraction)
- Timeout: 30 seconds per page
- Automatic removal of navigation, ads, and boilerplate

The scraper extracts:
- Page title from metadata
- Full markdown content
- Word count for analysis`,
  },
};

// ─── Workflows ───────────────────────────────────────────────────────

export const WORKFLOWS: Record<WorkflowId, WorkflowDefinition> = {
  web_crawler: {
    id: 'web_crawler',
    name: 'Web Crawler',
    description: 'Discovers and scrapes vendor websites, then uploads content to Gemini for intelligent querying.',
    icon: 'globe',
    steps: [
      {
        id: 'cache_check',
        label: 'Cache Check',
        description: 'Check Firestore for fresh data (< 24hrs). Skip collection if cache is valid.',
        service: 'API',
        icon: 'database',
        color: '#3B82F6',
        promptIds: [],
      },
      {
        id: 'tavily_discovery',
        label: 'URL Discovery',
        description: 'Two-phase search: documentation pages first, then general pages. Prioritizes docs, APIs, and guides.',
        service: 'Collector',
        icon: 'search',
        color: '#3B82F6',
        promptIds: ['tavily_search_config'],
      },
      {
        id: 'firecrawl_scraping',
        label: 'Page Scraping',
        description: 'Scrape each discovered URL and extract clean markdown content using Firecrawl.',
        service: 'Collector',
        icon: 'scrape',
        color: '#3B82F6',
        promptIds: [],
      },
      {
        id: 'processor_upload',
        label: 'Content Upload',
        description: 'Combine all pages into a single document and upload to Gemini File Search API.',
        service: 'Processor',
        icon: 'upload',
        color: '#8B5CF6',
        promptIds: [],
      },
      {
        id: 'gemini_store',
        label: 'Knowledge Store',
        description: 'Content stored in Gemini, ready for natural language queries.',
        service: 'Processor',
        icon: 'database',
        color: '#8B5CF6',
        promptIds: [],
      },
    ],
  },

  report_generation: {
    id: 'report_generation',
    name: 'Report Generation',
    description: 'Generates comprehensive vendor comparison reports across selected themes.',
    icon: 'file',
    steps: [
      {
        id: 'select_themes',
        label: 'Select Themes',
        description: 'User selects from 16 available themes to include in the report.',
        service: 'API',
        icon: 'list',
        color: '#10B981',
        promptIds: [],
      },
      {
        id: 'query_vendors',
        label: 'Query Vendors',
        description: 'For each theme, query every vendor\'s knowledge base using the theme prompt.',
        service: 'API + Processor',
        icon: 'search',
        color: '#10B981',
        promptIds: [
          'theme_company_overview', 'theme_products_services', 'theme_pricing_model',
          'theme_target_market', 'theme_competitive_advantages', 'theme_technology_stack',
          'theme_integration_capabilities', 'theme_security_compliance',
          'theme_customer_testimonials', 'theme_leadership_team',
          'theme_funding_financials', 'theme_partnerships',
          'theme_geographic_presence', 'theme_support_sla',
          'theme_roadmap_vision', 'theme_risk_factors',
        ],
      },
      {
        id: 'synthesize_themes',
        label: 'Synthesize Themes',
        description: 'Synthesize vendor responses for each theme into a cohesive summary.',
        service: 'API + Processor',
        icon: 'merge',
        color: '#10B981',
        promptIds: ['report_theme_synthesis'],
      },
      {
        id: 'executive_summary',
        label: 'Executive Summary',
        description: 'Generate an executive summary capturing key insights across all themes.',
        service: 'API + Processor',
        icon: 'sparkles',
        color: '#10B981',
        promptIds: ['report_executive_summary'],
      },
    ],
  },

  evaluation: {
    id: 'evaluation',
    name: 'Vendor Evaluation',
    description: 'Evaluates vendors against the 6-criterion AI framework: SEE, CHANGE, ADAPT, USE, LEARN, EXIT.',
    icon: 'brain',
    steps: [
      {
        id: 'system_setup',
        label: 'System Prompt',
        description: 'Initialize the evaluator with the framework philosophy, scoring methodology, and critical rules.',
        service: 'Evaluator',
        icon: 'brain',
        color: '#F59E0B',
        promptIds: ['evaluation_system_prompt'],
      },
      {
        id: 'data_gathering',
        label: 'Data Gathering',
        description: 'Query vendor knowledge base for information relevant to each of the 6 criteria (runs in parallel).',
        service: 'Evaluator + Processor',
        icon: 'search',
        color: '#F59E0B',
        promptIds: ['data_gathering_template', 'criterion_see', 'criterion_change', 'criterion_adapt', 'criterion_use', 'criterion_learn', 'criterion_exit'],
      },
      {
        id: 'structured_evaluation',
        label: 'Structured Evaluation',
        description: 'Evaluate each criterion using structured output: score, assessment, trade-offs, and questions.',
        service: 'Evaluator',
        icon: 'brain',
        color: '#F59E0B',
        promptIds: ['structured_eval_template'],
      },
      {
        id: 'exec_summary',
        label: 'Executive Summary',
        description: 'Generate an executive summary with key takeaway, sentiment, and narrative paragraphs.',
        service: 'Evaluator',
        icon: 'sparkles',
        color: '#F59E0B',
        promptIds: ['executive_summary_prompt'],
      },
      {
        id: 'key_questions',
        label: 'Key Questions',
        description: 'Generate 5-6 strategic questions tailored to the specific vendor evaluation results.',
        service: 'Evaluator',
        icon: 'chat',
        color: '#F59E0B',
        promptIds: ['key_questions_prompt'],
      },
    ],
  },

  conversation: {
    id: 'conversation',
    name: 'Vendor Chat',
    description: 'Enables natural language Q&A across multiple vendors in a workspace.',
    icon: 'chat',
    steps: [
      {
        id: 'user_message',
        label: 'User Message',
        description: 'User sends a question about their vendors in the workspace chat.',
        service: 'API',
        icon: 'chat',
        color: '#EC4899',
        promptIds: [],
      },
      {
        id: 'query_each_vendor',
        label: 'Query Vendors',
        description: 'Query each vendor\'s Gemini knowledge base with the user\'s question.',
        service: 'API + Processor',
        icon: 'search',
        color: '#EC4899',
        promptIds: [],
      },
      {
        id: 'synthesize_response',
        label: 'Synthesize Response',
        description: 'Combine vendor responses into a comprehensive answer that cites specific vendors.',
        service: 'API + Processor',
        icon: 'sparkles',
        color: '#EC4899',
        promptIds: ['conversation_synthesis'],
      },
    ],
  },
};

export const WORKFLOW_ORDER: WorkflowId[] = ['web_crawler', 'report_generation', 'evaluation', 'conversation'];

// ─── Helpers ─────────────────────────────────────────────────────────

export function getPromptsByCategory(category: PromptCategory): PromptDefinition[] {
  return Object.values(PROMPTS).filter(p => p.category === category);
}

export function getPromptsByWorkflow(workflowId: WorkflowId): PromptDefinition[] {
  return Object.values(PROMPTS).filter(p => p.usedIn.includes(workflowId));
}

export function getAllPrompts(): PromptDefinition[] {
  return Object.values(PROMPTS);
}

export function getPromptById(id: string): PromptDefinition | undefined {
  return PROMPTS[id];
}
